import type { KeyboardEvent } from "react";
import type { RowData } from "@tanstack/react-table";
import type { DataGridInstance } from "../types";
import type { GridController } from "./gridController";
import { normalizeKey } from "./keyMap";
import {
    findFirstFocusableRow,
    getFocusedAddress,
    getNavigableColumnIds,
    getRowModeColumnId,
    moveHeaderFocus,
} from "./navigation";
import { focusTableRow } from "./useAutoFocus";

/**
 * Header keys: arrows, Home and End move between header cells, and ArrowDown
 * goes back to the body's focused row or cell (the first focusable row when
 * none is rendered), in the header's column when cell navigation can land
 * there. Modified keys stay with the browser (Alt+ArrowLeft goes Back).
 * Returns whether the key was handled.
 */
export const runHeaderKey = <TData extends RowData>({
    event,
    instance,
    controller,
}: {
    event: KeyboardEvent<HTMLDivElement>;
    instance: DataGridInstance<TData>;
    controller: GridController;
}) => {
    const { table } = instance;
    const { key, isMod, isShift, isAlt, hasOtherModifier } =
        normalizeKey(event);
    if (
        isMod ||
        isShift ||
        isAlt ||
        hasOtherModifier ||
        !(event.target instanceof HTMLElement)
    ) {
        return false;
    }
    const from = event.target;
    const { root } = controller;
    switch (key) {
        case "arrowleft":
            return moveHeaderFocus({ root, from, to: -1 });
        case "arrowright":
            return moveHeaderFocus({ root, from, to: 1 });
        case "home":
            return moveHeaderFocus({ root, from, to: "first" });
        case "end":
            return moveHeaderFocus({ root, from, to: "last" });
        case "arrowdown": {
            const rows = table.getRowModel().rows;
            const current = getFocusedAddress(table.atoms.cellSelection.get());
            const navigableColumnIds = getNavigableColumnIds(table);
            const headerColumnId =
                from.closest<HTMLElement>("[data-column-id]")?.dataset.columnId;
            const navigableHeaderColumnId =
                headerColumnId !== undefined &&
                navigableColumnIds.includes(headerColumnId)
                    ? headerColumnId
                    : undefined;
            const isCurrentVisible =
                current !== null && controller.rowIndexById.has(current.rowId);
            const rowId = isCurrentVisible
                ? current.rowId
                : findFirstFocusableRow(rows, instance.rowKinds.isSeparator)
                      ?.id;
            const columnId = instance.hasCellNavigation
                ? (navigableHeaderColumnId ??
                  current?.columnId ??
                  navigableColumnIds[0] ??
                  null)
                : (current?.columnId ?? getRowModeColumnId(table));
            if (!rowId || columnId === null) {
                return false;
            }
            focusTableRow({
                instance,
                controller,
                rowId,
                columnId: instance.hasCellNavigation ? columnId : undefined,
            });
            return true;
        }
        default:
            return false;
    }
};
